import { getOperatorData } from '../api';

/**
 * Whether a failed operator load means the link's operator does not exist: razor answers 404 for an
 * unknown operator and 400 for a missing or malformed id (razor-backend#1584), and a 200 without an
 * `id` is no operator either. Anything else (5xx, a timeout, offline) is a passing failure.
 */
export const isInvalidOperatorError = (error) =>
  error?.invalidOperator === true || error?.status === 404 || error?.status === 400;

/** The id of the cached operator: a full object, or the bare id older pages stored; null if none or unreadable. */
const cachedOperatorId = () => {
  const raw = localStorage.getItem('operator');
  if (raw == null) return null;
  try {
    const cached = JSON.parse(raw);
    return cached && typeof cached === 'object' ? cached.id ?? null : cached;
  } catch (e) {
    return raw;
  }
};

/**
 * Loads a link's operator (web-startup) and caches the whole response as `operator`, the shape
 * LoginDialog and the waiver pages read. On any failure a cached operator that is not this one is
 * removed, so an operator left over from an earlier link is never used for this one (#41) — even when
 * razor answers an unknown id with a 500. When this operator does not exist (404/400) the cache is
 * removed whatever it holds; `forgetOnInvalid: false` leaves that case to the caller. A passing failure
 * keeps a cached operator with this id. Nothing is written once `isCancelled()` is true.
 */
export const loadOperator = async (operatorId, { forgetOnInvalid = true, isCancelled = () => false } = {}) => {
  try {
    const operator = await getOperatorData(operatorId);
    if (!operator?.id) {
      const error = new Error('No operator in the web-startup response');
      error.invalidOperator = true;
      throw error;
    }
    if (!isCancelled()) localStorage.setItem('operator', JSON.stringify(operator));
    return operator;
  } catch (error) {
    if (!isCancelled()) {
      const cachedId = cachedOperatorId();
      const otherOperator = cachedId != null && String(cachedId) !== String(operatorId);
      if (otherOperator || (forgetOnInvalid && isInvalidOperatorError(error))) localStorage.removeItem('operator');
    }
    throw error;
  }
};

/** A link's fleet must belong to its operator. A fleet that does not say which operator it has is not judged. */
export const fleetBelongsToOperator = (fleet, operatorId) =>
  fleet?.operator_id == null || String(fleet.operator_id) === String(operatorId);
